/* ============================================================
   GlyphoBlock — Theme JS
   Cart drawer (Shopify AJAX), email modal, mobile nav,
   intersection observer animations, count-up, FAQ accordion
   ============================================================ */

(function () {
  'use strict';

  // ───────────────────────────────────────────────────
  // 1. INTERSECTION OBSERVER — scroll-triggered animations
  // ───────────────────────────────────────────────────
  function initScrollAnimations() {
    const targets = document.querySelectorAll('[data-animate], [data-stagger]');
    if (!('IntersectionObserver' in window) || targets.length === 0) {
      targets.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });
    targets.forEach(el => io.observe(el));
  }

  // ───────────────────────────────────────────────────
  // 2. COUNT-UP — animated number counters
  // ───────────────────────────────────────────────────
  function initCountUp() {
    const elements = document.querySelectorAll('[data-count-target]');
    if (!('IntersectionObserver' in window) || elements.length === 0) return;

    const animate = (el) => {
      const target = parseInt(el.dataset.countTarget, 10);
      const duration = parseInt(el.dataset.countDuration, 10) || 1800;
      const suffix = el.dataset.countSuffix || '';
      const start = performance.now();
      const step = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = Math.round(eased * target) + suffix;
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animate(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });

    elements.forEach(el => {
      el.textContent = '0' + (el.dataset.countSuffix || '');
      io.observe(el);
    });
  }

  // ───────────────────────────────────────────────────
  // 3. STICKY NAV — add scrolled state on scroll
  // ───────────────────────────────────────────────────
  function initStickyNav() {
    const nav = document.querySelector('[data-sticky-nav]');
    if (!nav) return;
    const onScroll = () => {
      nav.classList.toggle('is-scrolled', window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ───────────────────────────────────────────────────
  // 4. MOBILE MENU
  // ───────────────────────────────────────────────────
  function initMobileMenu() {
    const openBtn = document.querySelector('[data-mobile-menu-open]');
    const closeBtn = document.querySelector('[data-mobile-menu-close]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!openBtn || !menu) return;

    const open = () => {
      menu.classList.add('is-open');
      document.body.classList.add('no-scroll');
    };
    const close = () => {
      menu.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    };

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    menu.querySelectorAll('a').forEach(a => a.addEventListener('click', close));
  }

  // ───────────────────────────────────────────────────
  // 5. FAQ ACCORDION
  // ───────────────────────────────────────────────────
  function initFAQ() {
    document.querySelectorAll('[data-faq-item]').forEach(item => {
      const button = item.querySelector('[data-faq-toggle]');
      if (!button) return;
      button.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');
        item.classList.toggle('is-open', !isOpen);
        button.setAttribute('aria-expanded', String(!isOpen));
        // Toggle plus/x icons
        const plus = button.querySelector('[data-faq-plus]');
        const x = button.querySelector('[data-faq-x]');
        if (plus && x) {
          plus.style.display = isOpen ? 'inline-flex' : 'none';
          x.style.display = isOpen ? 'none' : 'inline-flex';
        }
      });
    });
  }

  // ───────────────────────────────────────────────────
  // 6. FEATURE ACCORDION (product section)
  // ───────────────────────────────────────────────────
  function initFeatureAccordion() {
    const accordions = document.querySelectorAll('[data-feature-accordion]');
    accordions.forEach((acc, idx) => {
      const button = acc.querySelector('[data-feature-toggle]');
      if (!button) return;
      // First one open by default
      if (idx === 0) acc.classList.add('is-open');
      button.addEventListener('click', () => {
        const wasOpen = acc.classList.contains('is-open');
        // Close all
        accordions.forEach(a => a.classList.remove('is-open'));
        if (!wasOpen) acc.classList.add('is-open');
      });
    });
  }

  // ───────────────────────────────────────────────────
  // 7. PRODUCT THUMBNAIL GALLERY
  // ───────────────────────────────────────────────────
  function initThumbnails() {
    const thumbs = document.querySelectorAll('[data-thumb]');
    const mainImg = document.querySelector('[data-main-image]');
    const mainImgBottle = document.querySelector('[data-main-image-bottle]');
    if (thumbs.length === 0 || !mainImg) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        const src = thumb.dataset.thumbSrc;
        const alt = thumb.dataset.thumbAlt;
        const isBottle = thumb.dataset.thumbBottle === 'true';

        if (isBottle && mainImgBottle) {
          mainImgBottle.src = src;
          mainImgBottle.alt = alt;
          mainImgBottle.style.display = 'block';
          mainImg.style.display = 'none';
        } else {
          mainImg.src = src;
          mainImg.alt = alt;
          mainImg.style.display = 'block';
          if (mainImgBottle) mainImgBottle.style.display = 'none';
        }
      });
    });
  }

  // ───────────────────────────────────────────────────
  // 8. PRICING CARD SELECTION
  // ───────────────────────────────────────────────────
  function initPricingCards() {
    const cards = document.querySelectorAll('[data-pricing-card]');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        const variantInput = document.querySelector('[data-variant-input]');
        const qtyInput = document.querySelector('[data-quantity-input]');
        const sellingPlanInput = document.querySelector('[data-selling-plan-input]');
        if (variantInput) variantInput.value = card.dataset.variantId || '';
        if (qtyInput) qtyInput.value = card.dataset.bottles || '1';
        if (sellingPlanInput) sellingPlanInput.value = card.dataset.sellingPlan || '';
      });
    });
  }

  // ───────────────────────────────────────────────────
  // 8b. PROMO PRICING (strike-through when promo code is active)
  // Shows for visitors who came through /discount/CODE or ?discount=CODE, or
  // for everyone when "Apply to all visitors" is on. Before showing anything,
  // the code is checked against a throwaway cart, so if the discount is
  // deactivated/expired in Shopify the promo disappears on its own.
  // ───────────────────────────────────────────────────
  function initPromoPricing() {
    const config = document.querySelector('[data-promo-config]');
    if (!config) return;
    const code = (config.dataset.code || '').trim();
    const percent = parseFloat(config.dataset.percent) || 0;
    const allVisitors = config.dataset.allVisitors === 'true';
    if (!code || !percent) return;

    const matches = (value) => !!value && value.split(',').some(v => v.trim().toUpperCase() === code.toUpperCase());
    const cookieMatch = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    const cookieCode = cookieMatch ? decodeURIComponent(cookieMatch[1]) : '';

    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('discount');
    if (!allVisitors && matches(urlCode) && !matches(cookieCode)) {
      // Route through Shopify's discount link so the code is applied at checkout.
      params.delete('discount');
      const query = params.toString();
      const redirect = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
      window.location.replace('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(redirect));
      return;
    }
    if (!allVisitors && !matches(cookieCode) && !matches(urlCode)) return;

    isCodeActive(code).then(active => {
      if (!active) return;
      showPromoPrices(percent);
      if (allVisitors && !matches(cookieCode)) applyCodeToVisitor(code);
    });
  }

  // Ask Shopify whether the code currently applies, using a cookieless cart so
  // the visitor's own cart is untouched. Cached for 10 minutes per session.
  function isCodeActive(code) {
    const cacheKey = 'promo-active:' + code.toUpperCase();
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && Date.now() - cached.at < 10 * 60 * 1000) return Promise.resolve(cached.active);
    } catch (e) {}

    const variantId = document.querySelector('[data-pricing-card]')?.dataset.variantId;
    if (!variantId) return Promise.resolve(false);

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: { [variantId]: 1 }, discount: code })
    })
      .then(r => r.json())
      .then(cart => {
        const active = (cart.discount_codes || []).some(d => d.applicable && d.code.toUpperCase() === code.toUpperCase());
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ active, at: Date.now() })); } catch (e) {}
        return active;
      })
      .catch(() => false);
  }

  // Same as visiting /discount/CODE: sets the discount cookie and attaches the
  // code to the visitor's cart so it carries through to checkout.
  function applyCodeToVisitor(code) {
    fetch('/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart.js', { headers: { 'Accept': 'application/json' } })
      .then(() => fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: code })
      }))
      .then(() => Cart.refresh())
      .catch(err => console.error('Promo apply failed:', err));
  }

  function showPromoPrices(percent) {
    const fmt = (cents) => '$' + (cents / 100).toFixed(2);
    const compareCents = 5999;

    document.querySelectorAll('[data-promo-price]').forEach(block => {
      const unit = parseInt(block.dataset.unitCents, 10);
      if (!unit) return;
      // Shopify rounds the discount amount down, so $59.99 at 50% off checks out at $30.00.
      const sale = unit - Math.floor(unit * percent / 100);
      const amount = block.querySelector('[data-promo-amount]');
      if (amount) amount.textContent = fmt(sale);
      block.querySelectorAll('[data-promo-strike], [data-promo-note]').forEach(el => { el.style.display = ''; });

      const badge = block.closest('[data-pricing-card]')?.querySelector('[data-promo-badge]');
      if (badge) {
        badge.textContent = 'SAVE ' + Math.round((compareCents - sale) / compareCents * 100) + '%';
        if (badge.hasAttribute('data-promo-only')) badge.style.display = '';
      }
    });

    const banner = document.querySelector('[data-promo-banner]');
    if (banner) banner.style.display = 'flex';
  }

  // ───────────────────────────────────────────────────
  // 9. CART DRAWER (Shopify AJAX cart API)
  // ───────────────────────────────────────────────────
  const Cart = {
    el: null,
    backdrop: null,
    itemsContainer: null,
    emptyState: null,
    footer: null,
    subtotalEl: null,
    countEl: null,

    init() {
      this.el = document.querySelector('[data-cart-drawer]');
      this.backdrop = document.querySelector('[data-cart-backdrop]');
      this.itemsContainer = document.querySelector('[data-cart-items]');
      this.emptyState = document.querySelector('[data-cart-empty]');
      this.footer = document.querySelector('[data-cart-footer]');
      this.subtotalEl = document.querySelector('[data-cart-subtotal]');
      this.countEl = document.querySelector('[data-cart-count]');

      if (!this.el) return;

      // Open cart triggers
      document.querySelectorAll('[data-open-cart]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });

      // Close cart triggers
      document.querySelectorAll('[data-close-cart]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.preventDefault(); this.close(); });
      });
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());

      // Add to cart button
      const addBtn = document.querySelector('[data-add-to-cart]');
      if (addBtn) {
        addBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.addToCart();
        });
      }

      // Checkout button
      const checkoutBtn = document.querySelector('[data-checkout]');
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', (e) => {
          e.preventDefault();
          window.location.href = '/checkout';
        });
      }

      // Initial cart fetch
      this.refresh();
    },

    open() {
      if (!this.el) return;
      this.el.classList.add('is-open');
      if (this.backdrop) this.backdrop.classList.add('is-open');
      document.body.classList.add('no-scroll');
    },

    close() {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      if (this.backdrop) this.backdrop.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    },

    addToCart() {
      const variantInput = document.querySelector('[data-variant-input]');
      const qtyInput = document.querySelector('[data-quantity-input]');
      const sellingPlanInput = document.querySelector('[data-selling-plan-input]');
      const variantId = variantInput ? variantInput.value : null;
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      const sellingPlan = sellingPlanInput ? sellingPlanInput.value : '';

      if (!variantId) {
        console.warn('No variant selected');
        return;
      }

      var payload = { id: variantId, quantity: qty };
      if (sellingPlan) {
        payload.selling_plan = sellingPlan;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(r => r.json())
        .then(() => this.refresh())
        .then(() => this.open())
        .catch(err => console.error('Cart add failed:', err));
    },

    refresh() {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(r => r.json())
        .then(cart => this.render(cart))
        .catch(err => console.error('Cart fetch failed:', err));
    },

    render(cart) {
      if (!this.itemsContainer) return;

      // Update count badge
      if (this.countEl) {
        this.countEl.textContent = cart.item_count;
        this.countEl.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
      }

      // Empty state vs items
      if (cart.item_count === 0) {
        this.itemsContainer.innerHTML = '';
        if (this.emptyState) this.emptyState.style.display = 'flex';
        if (this.footer) this.footer.style.display = 'none';
        return;
      }

      if (this.emptyState) this.emptyState.style.display = 'none';
      if (this.footer) this.footer.style.display = 'flex';

      // Render items
      this.itemsContainer.innerHTML = cart.items.map(item => this.itemHTML(item)).join('');

      // Wire up quantity controls
      this.itemsContainer.querySelectorAll('[data-cart-qty-up]').forEach(btn => {
        btn.addEventListener('click', () => this.updateQty(btn.dataset.line, parseInt(btn.dataset.qty, 10) + 1));
      });
      this.itemsContainer.querySelectorAll('[data-cart-qty-down]').forEach(btn => {
        btn.addEventListener('click', () => this.updateQty(btn.dataset.line, parseInt(btn.dataset.qty, 10) - 1));
      });
      this.itemsContainer.querySelectorAll('[data-cart-remove]').forEach(btn => {
        btn.addEventListener('click', () => this.updateQty(btn.dataset.line, 0));
      });

      // Update subtotal
      if (this.subtotalEl) {
        const total = '$' + (cart.total_price / 100).toFixed(2);
        if (cart.original_total_price > cart.total_price) {
          this.subtotalEl.innerHTML = '<span style="font-family:Inter,sans-serif;font-weight:400;font-size:15px;color:rgba(245,240,232,0.35);text-decoration:line-through;margin-right:8px;">$' + (cart.original_total_price / 100).toFixed(2) + '</span>' + total;
        } else {
          this.subtotalEl.textContent = total;
        }
      }
      const itemCountText = document.querySelector('[data-cart-subtotal-label]');
      if (itemCountText) {
        const noun = cart.item_count === 1 ? 'item' : 'items';
        itemCountText.textContent = 'Subtotal (' + cart.item_count + ' ' + noun + ')';
      }
    },

    itemHTML(item) {
      const TEAL = '#4FB8C4';
      const NAVY = '#0A1628';
      const NAVY_CARD = '#0e1f38';
      const price = (item.final_line_price / 100).toFixed(2);
      const unit = (item.final_price / 100).toFixed(2);
      const strike = (cents, size) => `<span style="font-family:Inter,sans-serif;font-weight:400;font-size:${size}px;color:rgba(245,240,232,0.35);text-decoration:line-through;margin-right:6px;">$${(cents / 100).toFixed(2)}</span>`;
      const unitStrike = item.original_price > item.final_price ? strike(item.original_price, 13) : '';
      const lineStrike = item.original_line_price > item.final_line_price ? strike(item.original_line_price, 14) : '';
      const img = item.image || '/assets/bottle.webp';
      const isAutoship = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      const autoshipName = isAutoship ? item.selling_plan_allocation.selling_plan.name : '';
      return `
        <div style="background:${NAVY_CARD};border:1px solid ${isAutoship ? 'rgba(79,184,196,0.35)' : 'rgba(79,184,196,0.15)'};border-radius:10px;padding:14px;">
          ${isAutoship ? `
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;padding:6px 10px;background:rgba(79,184,196,0.1);border:1px solid rgba(79,184,196,0.2);border-radius:6px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${TEAL}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
            <span style="font-family:'Inter Tight',sans-serif;font-weight:700;font-size:11px;color:${TEAL};text-transform:uppercase;letter-spacing:0.05em;">Autoship – ${autoshipName}</span>
          </div>` : ''}
          <div style="display:flex;gap:14px;align-items:flex-start;">
            <div style="width:72px;height:72px;border-radius:8px;background:${NAVY};border:1px solid rgba(79,184,196,0.2);flex-shrink:0;display:flex;align-items:center;justify-content:center;overflow:hidden;">
              <img src="${img}" alt="${item.product_title}" style="width:80%;object-fit:contain;filter:drop-shadow(0 4px 8px rgba(79,184,196,0.2));"/>
            </div>
            <div style="flex:1;min-width:0;">
              <p style="font-family:'Inter Tight',sans-serif;font-weight:700;font-size:15px;color:#f5f0e8;line-height:1.3;margin:0 0 4px;">${item.product_title}</p>
              <p style="font-family:Inter,sans-serif;font-size:13px;color:rgba(245,240,232,0.5);line-height:1.3;margin:0 0 6px;">${item.variant_title || ''}</p>
              <p style="font-family:'Inter Tight',sans-serif;font-weight:700;font-size:15px;color:${TEAL};margin:0;">${unitStrike}$${unit}</p>
            </div>
            <button data-cart-remove data-line="${item.key}" style="background:none;border:none;cursor:pointer;color:rgba(245,240,232,0.25);display:flex;align-items:center;padding:2px;flex-shrink:0;" aria-label="Remove item">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
            </button>
          </div>
          <div style="display:flex;align-items:center;justify-content:space-between;margin-top:14px;padding-top:12px;border-top:1px solid rgba(255,255,255,0.06);">
            <div style="display:flex;align-items:center;">
              <button data-cart-qty-down data-line="${item.key}" data-qty="${item.quantity}" style="width:32px;height:32px;border:1px solid rgba(255,255,255,0.12);border-radius:6px 0 0 6px;background:${NAVY};color:#f5f0e8;cursor:pointer;display:flex;align-items:center;justify-content:center;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              <div style="min-width:42px;height:32px;border:1px solid rgba(255,255,255,0.12);border-left:none;border-right:none;background:${NAVY};display:flex;align-items:center;justify-content:center;font-family:Inter,sans-serif;font-weight:600;font-size:14px;color:#f5f0e8;padding:0 8px;">${item.quantity}</div>
              <button data-cart-qty-up data-line="${item.key}" data-qty="${item.quantity}" style="width:32px;height:32px;border:1px solid rgba(255,255,255,0.12);border-radius:0 6px 6px 0;background:${NAVY};color:#f5f0e8;cursor:pointer;display:flex;align-items:center;justify-content:center;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
            </div>
            <span style="font-family:'Inter Tight',sans-serif;font-weight:800;font-size:18px;color:#f5f0e8;letter-spacing:-0.02em;">${lineStrike}$${price}</span>
          </div>
        </div>
      `;
    },

    updateQty(lineKey, qty) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: qty })
      })
        .then(r => r.json())
        .then(cart => this.render(cart))
        .catch(err => console.error('Cart update failed:', err));
    }
  };

  // ───────────────────────────────────────────────────
  // 10. EMAIL CAPTURE MODAL
  // ───────────────────────────────────────────────────
  const EmailModal = {
    LS_KEY: 'glyphoblock_popup_subscribed',
    el: null,
    submitted: false,

    init() {
      this.el = document.querySelector('[data-email-modal]');
      if (!this.el) return;

      const closeBtn = this.el.querySelector('[data-email-close]');
      const backdrop = this.el;
      const form = this.el.querySelector('form');

      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.el.classList.contains('is-open')) this.close();
      });

     if (form) {
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const emailInput = form.querySelector('input[type=email]');
    const email = emailInput ? emailInput.value.trim() : '';
    if (!email) return;

    const KLAVIYO_PUBLIC_KEY = 'RqvCew';
    const KLAVIYO_LIST_ID = 'TbKfBL';

    // POST to Klaviyo Client API — subscribes profile to list and triggers welcome flow
    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_PUBLIC_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Website Popup — 20% Off',
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          },
          relationships: {
            list: {
              data: { type: 'list', id: KLAVIYO_LIST_ID }
            }
          }
        }
      })
    }).catch(err => console.warn('Klaviyo subscribe error:', err));

    // Persist dismissal and show success regardless — the API call is fire-and-forget
    try { localStorage.setItem(this.LS_KEY, 'true'); } catch (e) {}
    const formView = this.el.querySelector('[data-email-form-view]');
    const successView = this.el.querySelector('[data-email-success-view]');
    if (formView && successView) {
      formView.style.display = 'none';
      successView.style.display = 'flex';
    }
  });
}

      // Trigger logic — 35s OR 45% scroll, whichever first, unless dismissed
      if (this.isDismissed()) return;

      const timer = setTimeout(() => this.maybeShow(), 35000);

      const onScroll = () => {
        if (this.isDismissed()) {
          window.removeEventListener('scroll', onScroll);
          return;
        }
        const scrolled = window.scrollY / (document.body.scrollHeight - window.innerHeight);
        if (scrolled >= 0.45) {
          clearTimeout(timer);
          window.removeEventListener('scroll', onScroll);
          this.maybeShow();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    },

    isDismissed() {
      try { return localStorage.getItem(this.LS_KEY) === 'true'; }
      catch (e) { return false; }
    },

    isInBuyingFlow() {
      const offer = document.getElementById('offer');
      if (!offer) return false;
      const rect = offer.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    },

    maybeShow() {
      if (this.isDismissed()) return;
      if (this.isInBuyingFlow()) return;
      this.el.classList.add('is-open');
      document.body.classList.add('no-scroll');
    },

    close() {
      this.el.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    }
  };

  // ───────────────────────────────────────────────────
  // 11. FOOTER MODALS (about, contact, etc.)
  // ───────────────────────────────────────────────────
  function initFooterModals() {
    document.querySelectorAll('[data-footer-modal-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const target = btn.dataset.footerModalOpen;
        const modal = document.querySelector(`[data-footer-modal="${target}"]`);
        if (modal) {
          modal.classList.add('is-open');
          document.body.classList.add('no-scroll');
        }
      });
    });
    document.querySelectorAll('[data-footer-modal-close]').forEach(btn => {
      btn.addEventListener('click', () => {
        btn.closest('[data-footer-modal]').classList.remove('is-open');
        document.body.classList.remove('no-scroll');
      });
    });
    document.querySelectorAll('[data-footer-modal]').forEach(backdrop => {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) {
          backdrop.classList.remove('is-open');
          document.body.classList.remove('no-scroll');
        }
      });
    });
  }

  // ───────────────────────────────────────────────────
  // INIT
  // ───────────────────────────────────────────────────
  function init() {
    initScrollAnimations();
    initCountUp();
    initStickyNav();
    initMobileMenu();
    initFAQ();
    initFeatureAccordion();
    initThumbnails();
    initPricingCards();
    initPromoPricing();
    initFooterModals();
    Cart.init();
    EmailModal.init();

    // Lucide icons (loaded from CDN in theme.liquid)
    if (window.lucide && typeof window.lucide.createIcons === 'function') {
      window.lucide.createIcons();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
